import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewEncapsulation,
  ElementRef,
  HostListener,
  ViewChildren,
  QueryList,
} from '@angular/core';
import {
  Discount,
  Vehicle,
  VehicleType,
} from '@/core/domain/branches/models/branch.model';
import { TranslationsGetLanguageUseCase } from '@/core/domain/translation/usecases/translations-get-language.usecase';
import { CdnService } from '@/common/services/cdn/cdn.service';
import { ProgressBarComponent } from '@/ui/shared/components/progress-bar/progress-bar.component';
import { SwalComponent } from '@sweetalert2/ngx-sweetalert2';
import { CardVehicleComponent } from '@/ui/shared/ui/card-vehicle/card-vehicle.component';
import { BreakpointService } from '@/common/services/breakpoint/breakpoint.service';


@Component({
  selector: 'fe-vehicle-list',
  templateUrl: './vehicle-list.component.html',
  styleUrls: ['./vehicle-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class VehicleListComponent {
  @Input() vehicles: Vehicle[] = [];
  @Input() discountOffice?: Discount;
  @Input() international = false;
  @Input() rateWeekly = false;
  @Input() currency?: string;
  @Input() discounts: Discount[] = [];
  @Input() rentalDays?: number;
  @Input() freeDays?: number;
  @Input() vehicleTypes: VehicleType[] = [];
  @Input() selectedTypeId: number | null = null;
  @Input() progressBarDesktop?: ProgressBarComponent;

  @Output() toggleSidebar = new EventEmitter<void>();
  @Output() sortChange = new EventEmitter<string>();
  @Output() typeChange = new EventEmitter<number | null>();

  @ViewChildren(CardVehicleComponent)
  private readonly cards!: QueryList<CardVehicleComponent>;

  constructor(
    private readonly getLanguageUseCase: TranslationsGetLanguageUseCase,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly breakpoint: BreakpointService,
  ) {}

  public onDetailOpened(opened: CardVehicleComponent): void {
    if (this.breakpoint.current !== 'md') {
      return;
    }

    this.cards.forEach((card) => {
      if (card !== opened) {
        card.closeDetail();
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;
    this.elementRef.nativeElement
      .querySelectorAll<HTMLDetailsElement>('details.sort-dropdown[open]')
      .forEach((dropdown) => {
        if (!dropdown.contains(target)) {
          dropdown.open = false;
        }
      });
  }

  get hasFreeDays(): boolean {
    return (
      !this.international &&
      !!this.freeDays &&
      !!this.rentalDays &&
      this.rentalDays > this.freeDays
    );
  }

  get chargedDays(): number {
    return (this.rentalDays ?? 0) - (this.freeDays ?? 0);
  }

  get freeDaysTitleKey(): string {
    return this.freeDays === 1
      ? 'fe_step2_list_vehicle_free_days_title_singular'
      : 'fe_step2_list_vehicle_free_days_title_plural';
  }

  get visibleVehicleTypes(): VehicleType[] {
    return [...this.vehicleTypes]
      .filter((type) => !type.eliminado)
      .sort((a, b) => a.orden - b.orden);
  }

  getTypeName(type: VehicleType): string {
    return this.getLanguageUseCase.execute().language === 'en'
      ? type.nombre_en
      : type.nombre;
  }

  getTypeIcon(type: VehicleType): string {
    return type.image || CdnService.url('images/car.svg');
  }

  onTypeClick(type: VehicleType) {
    const typeId = this.selectedTypeId === type.id ? null : type.id;
    this.typeChange.emit(typeId);
  }

  getDiscountForMethod(method: string): Discount | null {
    return this.discounts.find((d) => d.method === method) || null;
  }

  onToggleSidebar() {
    this.toggleSidebar.emit();
  }

  selectedSort = 'featured';

  private readonly sortLabelKeys: { [key: string]: string } = {
    featured: 'fe_step2_list_vehicle_span_filter_option_featured',
    lowerPrice: 'fe_step2_list_vehicle_span_filter_option_minor',
    higherPrice: 'fe_step2_list_vehicle_span_filter_option_higher',
  };

  get selectedSortLabelKey(): string {
    return (
      this.sortLabelKeys[this.selectedSort] ||
      this.sortLabelKeys['featured']
    );
  }

  ngOnInit(): void {
    if (this.international) {
      this.selectedSort = 'lowerPrice';
    }
  }

  onSortChange(option: string) {
    this.selectedSort = option;
    this.sortChange.emit(option);
  }

  @Input() modalPayMethod1!: { fire: () => void };
}

